/**
 * voice.js — unified speech-to-text ("dictation") for BisaWofa text fields.
 *
 * Any button with `class="voice-mic-btn"` and `data-voice-target="<input-id>"`
 * becomes a microphone that transcribes speech into that input/textarea. A
 * single document-level delegated handler covers both static and
 * dynamically-created buttons (map popups, glass forms, etc).
 *
 * Two backends, selected automatically:
 *   - Native (Capacitor iOS/Android) → @capacitor-community/speech-recognition,
 *     which uses the platform speech engine (works on iOS, where the web
 *     Speech API does not exist).
 *   - Web (desktop/Android Chrome, Edge, Samsung Internet) → the browser's
 *     window.SpeechRecognition.
 *
 * If neither backend is available (e.g. iOS Safari as a pure PWA, or Firefox),
 * the mic buttons stay hidden via the absence of the `speech-available` body
 * class, and forms remain fully usable by typing.
 *
 * Recognition language follows the app's current i18n language
 * (getSpeechLang()), so users dictate in their own language.
 *
 * @module voice
 */

import { t, getSpeechLang } from './i18n.js';

let backend = null;          // 'native' | 'web' | null
let nativeSR = null;         // the Capacitor plugin (lazy-loaded)
let WebSR = null;            // window.SpeechRecognition constructor

let toast = null;
let hideTimer = null;
let activeBtn = null;        // the mic button currently listening
let webRecognition = null;   // active web SpeechRecognition instance

/**
 * Initialise voice input. Detects the platform, picks a backend, and wires the
 * delegated mic-button handler. Safe to call once at startup.
 */
export async function initVoiceInput() {
  // Detect native (Capacitor) vs web.
  let isNative = false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    isNative = Capacitor.isNativePlatform();
  } catch (_) { /* @capacitor/core absent in pure-web dev */ }

  if (isNative) {
    try {
      const mod = await import('@capacitor-community/speech-recognition');
      nativeSR = mod.SpeechRecognition;
      const res = await nativeSR.available().catch(() => ({ available: false }));
      if (res?.available) backend = 'native';
    } catch (err) {
      console.warn('[Voice] native speech plugin unavailable:', err?.message || err);
    }
  }

  if (!backend) {
    WebSR = window.SpeechRecognition || window.webkitSpeechRecognition || null;
    if (WebSR) backend = 'web';
  }

  if (!backend) {
    console.log('[Voice] No speech backend available — mic buttons stay hidden');
    return;                                  // buttons remain hidden (no body class)
  }

  document.body.classList.add('speech-available');
  _ensureToast();
  document.addEventListener('click', _onMicClick);
  console.log(`[Voice] Ready (backend: ${backend})`);
}

// ---------- delegated mic handler ----------

function _onMicClick(e) {
  const btn = e.target.closest('.voice-mic-btn');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  // Second tap on the active button → stop.
  if (btn === activeBtn) {
    _stopActive();
    toast?.classList.remove('visible');
    return;
  }
  _stopActive(); // stop any other session first

  const targetId = btn.getAttribute('data-voice-target');
  const target = targetId && document.getElementById(targetId);
  if (!target) return;

  if (backend === 'native') _startNative(btn, target);
  else _startWeb(btn, target);
}

function _stopActive() {
  if (backend === 'native' && activeBtn) {
    try { nativeSR.stop(); } catch (_) {}
  }
  if (webRecognition) {
    try { webRecognition.abort(); } catch (_) {}
    webRecognition = null;
  }
  if (activeBtn) {
    activeBtn.classList.remove('listening');
    activeBtn = null;
  }
}

// ---------- native backend ----------

async function _startNative(btn, target) {
  try {
    // Ensure microphone / speech permission.
    let perm = await nativeSR.checkPermissions().catch(() => null);
    if (!perm || perm.speechRecognition !== 'granted') {
      perm = await nativeSR.requestPermissions().catch(() => null);
    }
    if (!perm || perm.speechRecognition !== 'granted') {
      _showToast(t('voice.error'), 2500);
      return;
    }

    activeBtn = btn;
    btn.classList.add('listening');
    _showToast(t('voice.listening'), 8000);

    // One-shot dictation; resolves with the recognised alternatives.
    const { matches } = await nativeSR.start({
      language: getSpeechLang(),
      maxResults: 1,
      partialResults: false,
      popup: false,
    });
    _applyTranscript(target, matches && matches[0]);
  } catch (err) {
    const msg = String(err?.message || err);
    // A user-initiated stop() surfaces as an error on some platforms; ignore.
    if (!/abort|cancel|no match/i.test(msg)) {
      console.warn('[Voice] native recognition error:', msg);
      _showToast(t('voice.error'), 2500);
    }
  } finally {
    if (activeBtn === btn) {
      btn.classList.remove('listening');
      activeBtn = null;
    }
  }
}

// ---------- web backend ----------

function _startWeb(btn, target) {
  const recognition = new WebSR();
  recognition.lang = getSpeechLang();
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;
  recognition.continuous = false;

  webRecognition = recognition;
  activeBtn = btn;
  btn.classList.add('listening');
  _showToast(t('voice.listening'), 8000);

  recognition.onresult = (event) => {
    const transcript = event.results[0]?.[0]?.transcript?.trim();
    _applyTranscript(target, transcript);
  };
  recognition.onerror = (event) => {
    console.warn('[Voice] SpeechRecognition error:', event.error);
    if (event.error === 'no-speech') _showToast(t('voice.noResult'), 2500);
    else if (event.error !== 'aborted') _showToast(t('voice.error'), 2500);
  };
  recognition.onend = () => {
    if (activeBtn === btn) {
      btn.classList.remove('listening');
      activeBtn = null;
      webRecognition = null;
    }
  };

  try {
    recognition.start();
  } catch (err) {
    console.warn('[Voice] Could not start recognition:', err);
    _showToast(t('voice.error'), 2500);
    _stopActive();
  }
}

// ---------- shared ----------

/** Append a transcript to a field and notify listeners (validation etc.). */
function _applyTranscript(target, transcript) {
  if (transcript) {
    const prev = target.value;
    target.value = prev ? `${prev} ${transcript}` : transcript;
    target.dispatchEvent(new Event('input', { bubbles: true }));
    _showToast(transcript, 2500);
  } else {
    _showToast(t('voice.noResult'), 2500);
  }
}

function _ensureToast() {
  toast = document.querySelector('.voice-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'voice-toast';
    document.body.appendChild(toast);
  }
}

function _showToast(msg, duration = 2000) {
  if (!toast) return;
  clearTimeout(hideTimer);
  toast.textContent = msg;
  toast.classList.add('visible');
  hideTimer = setTimeout(() => toast.classList.remove('visible'), duration);
}

export default { initVoiceInput };
